import type { ReactElement } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { accentTokens } from '@/design/VizAccents';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import type { WastedTokenPoint } from './InsightsTypes';

interface InsightsWastedTokenChartProps {
    trend: WastedTokenPoint[];
    /** The selected window — every day in `[startIso, endIso]` is plotted, zero-filled where there's no data. */
    startIso: string;
    endIso: string;
}

const DAY_MS = 86_400_000;

interface WastedRow {
    date: string;
    productive: number;
    wasted: number;
}

function eachDay(startDay: string, endDay: string): string[] {
    const out: string[] = [];
    let t = Date.parse(`${startDay}T00:00:00Z`);
    const end = Date.parse(`${endDay}T00:00:00Z`);
    for (; t <= end; t += DAY_MS) {
        out.push(new Date(t).toISOString().slice(0, 10));
    }
    return out;
}

function toRows(trend: WastedTokenPoint[], startDay: string, endDay: string): WastedRow[] {
    const byDate = new Map(trend.map((point) => [point.date, point]));
    return eachDay(startDay, endDay).map((date) => {
        const point = byDate.get(date);
        return {
            date: new Date(`${date}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
            productive: point?.productive ?? 0,
            wasted: point?.wasted ?? 0,
        };
    });
}

/** Daily token volume as stacked bars — productive at the base, estimated-wasted stacked on top (sums to that day's total). */
export default function InsightsWastedTokenChart({ trend, startIso, endIso }: InsightsWastedTokenChartProps): ReactElement {
    const productive = { solid: 'var(--bm-color-brand-accent)' };
    const wasted = accentTokens('amber');
    const rows = toRows(trend, startIso.slice(0, 10), endIso.slice(0, 10));
    return (
        <ResponsiveContainer width="100%" height={220}>
            <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 0, left: 0 }} barCategoryGap="20%">
                <CartesianGrid vertical={false} stroke="var(--bm-color-border-subtle)" />
                <XAxis dataKey="date" tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis tickFormatter={(v: number) => formatCompact(v)} tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 10 }} tickLine={false} axisLine={false} width={44} />
                <Tooltip
                    formatter={(value: number, name: string) => [formatCompact(value), name]}
                    contentStyle={{ background: 'var(--bm-color-surface)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                    cursor={{ fill: 'var(--bm-color-zinc-hover)' }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                <Bar dataKey="productive" stackId="tok" name="Productive" fill={productive.solid} />
                <Bar dataKey="wasted" stackId="tok" name="Wasted (est.)" fill={wasted.solid} radius={[3, 3, 0, 0]} />
            </BarChart>
        </ResponsiveContainer>
    );
}
